import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LocalHospital as DiseaseIcon,
  MedicalServices as TreatmentIcon,
  ModelTraining as TrainingSampleIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  drawerWidth: number;
  isMobile: boolean;
  isTablet: boolean;
  mobileOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  drawerWidth,
  isMobile,
  isTablet,
  mobileOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Diseases', icon: <DiseaseIcon />, path: '/diseases' },
    { text: 'Treatments', icon: <TreatmentIcon />, path: '/treatments' },
    { text: 'Training', icon: <TrainingSampleIcon />, path: '/training-samples' },
    { text: 'Export', icon: <ExportIcon />, path: '/training-export' },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pt: isMobile ? 3 : 2 }}>
      <Box sx={{ overflow: 'auto', py: 2 }}>
        <List sx={{ px: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  px: 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: location.pathname === item.path ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          [`& .MuiDrawer-paper`]: {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRadius: '0 16px 16px 0',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  if (isTablet) {
    return null; // Hide sidebar on tablet for clean look
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: 96, // Start below floating navbar
          height: 'calc(100vh - 96px)',
          borderRadius: '0 16px 16px 0',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};